import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { LuLock, LuShieldCheck, LuEye, LuEyeOff } from 'react-icons/lu';
import { resetPassword } from '../../redux/reducers/authSlice';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = React.useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const token = searchParams.get('token');
    const { passwordLoading } = useSelector((state) => state.auth);

    const formik = useFormik({
        initialValues: {
            password: '',
            confirmPassword: ''
        },
        validationSchema: Yup.object({
            password: Yup.string()
                .min(8, 'Password must be at least 8 characters')
                .required('Required'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Passwords must match')
                .required('Required'),
        }),
        onSubmit: (values) => {
            dispatch(resetPassword({ token, newPassword: values.password })).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    navigate('/login');
                }
            });
        },
    });

    // If no token is present, we shouldn't show the form
    if (!token) {
        return (
            <div className="vh-100 d-flex align-items-center justify-content-center bg-light">
                <Card className="border-0 shadow-sm rounded-4 p-5 text-center" style={{ maxWidth: '400px' }}>
                    <h4 className="fw-bold text-danger">Invalid Link</h4>
                    <p className="text-muted">This password reset link is missing or malformed.</p>
                    <Button variant="brand" onClick={() => navigate('/login')} className="rounded-pill">Back to Login</Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="vh-100 d-flex align-items-center justify-content-center bg-light px-3">
            <Card className="border-0 shadow-sm rounded-4 p-4 p-md-5" style={{ maxWidth: '450px', width: '100%' }}>
                <div className="text-center mb-4">
                    <div className="bg-soft-success text-success rounded-circle d-inline-flex p-3 mb-3">
                        <LuShieldCheck size={32} />
                    </div>
                    <h3 className="fw-bold">Set New Password</h3>
                    <p className="text-muted small">
                        Almost there! Create a strong password to secure your account.
                    </p>
                </div>

                <Form onSubmit={formik.handleSubmit}>
                    {/* New Password */}
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-muted">New Password</Form.Label>
                        <InputGroup className="bg-light rounded-3 overflow-hidden border-0">
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                placeholder="Min 8 characters"
                                className="bg-light border-0 py-3 shadow-none"
                                {...formik.getFieldProps('password')}
                                isInvalid={formik.touched.password && !!formik.errors.password}
                            />
                            <Button
                                variant="light"
                                className="border-0 text-muted px-3"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                            </Button>
                            <Form.Control.Feedback type="invalid" tooltip>
                                {formik.errors.password}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>

                    {/* Confirm Password */}
                    <Form.Group className="mb-4">
                        <Form.Label className="small fw-bold text-muted">Confirm Password</Form.Label>
                        <Form.Control
                            type="password"
                            name="confirmPassword"
                            placeholder="Repeat new password"
                            className="bg-light border-0 py-3 shadow-none rounded-3"
                            {...formik.getFieldProps('confirmPassword')}
                            isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                        />
                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                            <div className="text-danger small mt-1">{formik.errors.confirmPassword}</div>
                        )}
                    </Form.Group>

                    <Button
                        variant="brand"
                        type="submit"
                        className="w-100 py-3 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                        disabled={passwordLoading}
                    >
                        {passwordLoading ? (
                            <Spinner size="sm" animation="border" />
                        ) : (
                            <>Reset Password <LuLock size={18} /></>
                        )}
                    </Button>
                </Form>
            </Card>
        </div>
    );
};

export default ResetPassword;
